import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { InvestmentOpportunity, investmentService } from '../services/investment.service';
import { CARD_GRADIENTS } from '../constants/cardGradients';
import type { Card } from '../components/cardSlider/Card';

interface UseInvestmentOpportunitiesOptions {
  activeFilter?: string | null;
  searchValue?: string;
}

export const useInvestmentOpportunities = ({ 
  activeFilter = null, 
  searchValue = '' 
}: UseInvestmentOpportunitiesOptions = {}) => {
  const { t } = useTranslation();
  const [opportunities, setOpportunities] = useState<InvestmentOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(false);

  useEffect(() => {
    const loadOpportunities = async () => {
      if (loadingRef.current) return;
      loadingRef.current = true;

      try {
        const data = await investmentService.getBestOpportunities();
        setOpportunities(data);
      } catch (error) {
        console.error('[useInvestmentOpportunities] Error:', error);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };

    loadOpportunities();
  }, []);

  const filteredOpportunities = useMemo(() => {
    return opportunities.filter(opportunity => {
      const matchesFilter = !activeFilter || opportunity.categories.includes(activeFilter);
      const matchesSearch = !searchValue || 
        opportunity.platform.toLowerCase().includes(searchValue.toLowerCase()) ||
        opportunity.type.toLowerCase().includes(searchValue.toLowerCase()) ||
        opportunity.network.toLowerCase().includes(searchValue.toLowerCase());
      
      return matchesFilter && matchesSearch;
    });
  }, [opportunities, activeFilter, searchValue]);

  const premiumDeals = useMemo(() => {
    if (!filteredOpportunities.length) return [];
    return [...filteredOpportunities].slice(0, 7);
  }, [filteredOpportunities]);

  const regularDeals = useMemo(() => {
    const premiumIds = new Set(premiumDeals.map(deal => deal.id));
    return filteredOpportunities.filter(deal => !premiumIds.has(deal.id));
  }, [filteredOpportunities, premiumDeals]);

  const cards = useMemo((): Card[] => {
    return Array.from({ length: 7 }, (_, index) => {
      const deal = premiumDeals[index];
      
      if (deal) {
        return {
          id: deal.id,
          balance: deal.apy + '%',
          cardNumber: deal.platform,
          cardType: index % 2 === 0 ? 'visa' as const : 'mastercard' as const,
          type: deal.type,
          network: deal.network,
          network_logo_url: deal.network_logo_url,
          logo_url: deal.logo_url,
          creditLimit: index % 2 === 0 ? {
            amount: parseFloat(String(deal.apy)),
            used: parseFloat(String(deal.apy)) * 0.8
          } : undefined,
          gradient: CARD_GRADIENTS[index % CARD_GRADIENTS.length]
        };
      } else {
        return {
          id: `empty-${index}`,
          balance: '0%',
          cardNumber: t('HOME_SCREEN.PREMIUM_DEALS.NO_DEALS_AVAILABLE'),
          cardType: index % 2 === 0 ? 'visa' as const : 'mastercard' as const,
          type: t('HOME_SCREEN.PREMIUM_DEALS.NO_DEALS'),
          network: 'None',
          network_logo_url: '',
          logo_url: '',
          gradient: CARD_GRADIENTS[index % CARD_GRADIENTS.length]
        };
      }
    });
  }, [premiumDeals, t]);

  return {
    opportunities,
    filteredOpportunities,
    premiumDeals,
    regularDeals,
    cards,
    loading
  };
};

